import { useState, useRef, useEffect } from 'react'
import { X, Upload, FileImage, AlertCircle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useSubmitUpgradeRequest, useReuploadUpgradeProof } from '@/hooks/use-subscription-upgrade'
import type { AvailablePlan } from '@/hooks/use-subscription-upgrade'

// ── Types ──────────────────────────────────────────────────────────────────────

interface UpgradeModalProps {
  open: boolean
  onClose: () => void
  selectedPlan: AvailablePlan
  currentPlanId: string | null
  /** When set, modal operates in re-upload mode (rejected request). */
  requestId?: string
  /** When true, modal operates in downgrade mode — no payment proof required. */
  isDowngrade?: boolean
}

// ── Platform payment info query ───────────────────────────────────────────────
// Reads from platform_settings (single row, id=1) — platform's own bank/QRIS info.

function usePlatformPaymentInfo() {
  return useQuery({
    queryKey: ['platform-settings'],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await (
        supabase
          .from('platform_settings')
          .select('payment_info, qris_image_url') as any
      )
        .eq('id', 1)
        .single()
      if (error) throw error
      return data as { payment_info: string | null; qris_image_url: string | null }
    },
  })
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatPrice(price: number | null) {
  if (!price) return 'Gratis'
  return (
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(price) + ' / bulan'
  )
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
const MAX_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB

// ── Component ──────────────────────────────────────────────────────────────────

export default function UpgradeModal({
  open,
  onClose,
  selectedPlan,
  currentPlanId,
  requestId,
  isDowngrade,
}: UpgradeModalProps) {
  const isReupload = !!requestId

  const [proofFile, setProofFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: paymentInfo, isLoading: paymentLoading } = usePlatformPaymentInfo()

  const submitMutation = useSubmitUpgradeRequest()
  const reuploadMutation = useReuploadUpgradeProof()

  const activeMutation = isReupload ? reuploadMutation : submitMutation
  const isPending = activeMutation.isPending
  const mutationError = activeMutation.error as Error | null

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setProofFile(null)
      setFileError(null)
      submitMutation.reset()
      reuploadMutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  // ── File handling ────────────────────────────────────────────────────────────

  function validateFile(file: File): string | null {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Format tidak didukung. Gunakan JPG, PNG, WebP, atau PDF.'
    }
    if (file.size > MAX_SIZE_BYTES) {
      return 'Ukuran file maksimal 10 MB.'
    }
    return null
  }

  function handleFileSelect(file: File) {
    const err = validateFile(file)
    if (err) {
      setFileError(err)
      setProofFile(null)
    } else {
      setFileError(null)
      setProofFile(file)
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileSelect(file)
  }

  // ── Submit ───────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    if (!isDowngrade && !proofFile) return

    if (isReupload && requestId) {
      await reuploadMutation.mutateAsync({ requestId, proofFile: proofFile! })
    } else {
      await submitMutation.mutateAsync({
        requestedPlanId: selectedPlan.id,
        currentPlanId,
        proofFile: proofFile ?? undefined,
      })
    }

    onClose()
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/60" onClick={isPending ? undefined : onClose} />

      <div className="relative z-10 w-full max-w-lg rounded-t-2xl sm:rounded-xl border border-border bg-card shadow-2xl max-h-[90dvh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="font-semibold text-sm">
              {isReupload
                ? 'Upload Ulang Bukti Pembayaran'
                : isDowngrade
                ? `Turunkan ke ${selectedPlan.name}`
                : `Upgrade ke ${selectedPlan.name}`}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatPrice(selectedPlan.price_idr)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="overflow-y-auto flex-1 px-5 py-5 space-y-5">
          {isDowngrade ? (
            /* ── Downgrade confirmation ── */
            <>
              <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm space-y-1">
                <p className="font-medium">Paket tujuan: {selectedPlan.name}</p>
                {selectedPlan.description && (
                  <p className="text-muted-foreground text-xs">{selectedPlan.description}</p>
                )}
              </div>

              <div className="flex items-start gap-2 rounded-lg border border-yellow-500/20 bg-yellow-500/5 px-4 py-3 text-sm text-yellow-700">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Permintaan downgrade tidak memerlukan bukti pembayaran. Setelah dikonfirmasi,
                  permintaanmu akan menunggu persetujuan admin.
                </span>
              </div>

              {mutationError && (
                <p className="flex items-center gap-1.5 text-xs text-destructive">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {mutationError.message ?? 'Terjadi kesalahan. Coba lagi.'}
                </p>
              )}
            </>
          ) : (
            /* ── Upgrade / re-upload flow ── */
            <>
              {/* Plan summary */}
              {!isReupload && (
                <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm space-y-1">
                  <p className="font-medium">Paket yang dipilih: {selectedPlan.name}</p>
                  {selectedPlan.description && (
                    <p className="text-muted-foreground text-xs">{selectedPlan.description}</p>
                  )}
                </div>
              )}

              {/* Payment info */}
              <div className="space-y-3">
                <p className="text-sm font-medium">Informasi Pembayaran</p>

                {paymentLoading ? (
                  <p className="text-xs text-muted-foreground">Memuat info pembayaran...</p>
                ) : (
                  <>
                    {paymentInfo?.payment_info ? (
                      <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                        <pre className="text-sm whitespace-pre-wrap text-foreground font-sans">
                          {paymentInfo.payment_info}
                        </pre>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 rounded-lg border border-yellow-500/20 bg-yellow-500/5 px-4 py-3 text-sm text-yellow-600">
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                          Info pembayaran belum diatur oleh admin. Hubungi admin platform untuk detail
                          pembayaran sebelum mengirim bukti transfer.
                        </span>
                      </div>
                    )}

                    {paymentInfo?.qris_image_url && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium">QRIS</p>
                        <div className="flex justify-start">
                          <div className="h-40 w-40 rounded-lg border border-border bg-white overflow-hidden flex items-center justify-center">
                            <img
                              src={paymentInfo.qris_image_url}
                              alt="QRIS"
                              className="max-h-full max-w-full object-contain p-2"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Proof upload */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Bukti Transfer</p>
                <p className="text-xs text-muted-foreground">
                  Upload screenshot/foto bukti pembayaran. Format: JPG, PNG, WebP, atau PDF. Maks 10 MB.
                </p>

                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer transition-colors px-4 py-8 ${
                    dragOver
                      ? 'border-primary/60 bg-primary/5'
                      : proofFile
                      ? 'border-green-500/40 bg-green-500/5'
                      : 'border-border bg-muted/20 hover:border-primary/40 hover:bg-muted/40'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    onChange={handleInputChange}
                    className="sr-only"
                  />

                  {proofFile ? (
                    <>
                      <FileImage className="h-8 w-8 text-green-500" />
                      <div className="text-center">
                        <p className="text-sm font-medium text-green-600 truncate max-w-[240px]">
                          {proofFile.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(proofFile.size / 1024).toFixed(0)} KB — klik untuk ganti
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">
                          Drag & drop atau <span className="text-primary font-medium">pilih file</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">JPG, PNG, WebP, PDF</p>
                      </div>
                    </>
                  )}
                </div>

                {/* File error */}
                {fileError && (
                  <p className="flex items-center gap-1.5 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {fileError}
                  </p>
                )}

                {/* Mutation error */}
                {mutationError && (
                  <p className="flex items-center gap-1.5 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {mutationError.message ?? 'Terjadi kesalahan. Coba lagi.'}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent disabled:opacity-50"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={(!isDowngrade && !proofFile) || isPending}
            className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isPending
              ? isDowngrade
                ? 'Mengirim...'
                : isReupload
                ? 'Mengirim ulang...'
                : 'Mengirim...'
              : isDowngrade
              ? 'Konfirmasi Downgrade'
              : isReupload
              ? 'Kirim Ulang Bukti'
              : 'Kirim Permintaan Upgrade'}
          </button>
        </div>
      </div>
    </div>
  )
}
